import React, { useState } from 'react';
import { Label, Alert } from 'reactstrap';
import useAxios from '../../../hooks/useAxios';
import { showToast } from '../../../components/toast/Toast';

export default function PersonalDataForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState({
    email: '',
    address: '',
    address_asper_ktp: 0,
    domicile_file: null,
    name: 'BPJS Kesehatan',
    id_number: '',
    faskes: '',
    file: null,
  });

  const isComplete = false;

  const api = useAxios();

  const [errors, setErrors] = useState({});

  const MAX_SIZE = 20 * 1024 * 1024; // 20MB

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    setForm((prev) => ({
      ...prev,

      [name]:
        type === 'checkbox'
          ? checked
            ? 1
            : 0 // ✅ convert ke 1/0
          : type === 'file'
          ? files[0]
          : value,
    }));

    // reset error saat user edit

    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const newErrors = {};

    // email

    if (!form.email) {
      newErrors.email = 'Email wajib diisi';
    } else if (!EMAIL_REGEX.test(form.email)) {
      newErrors.email = 'Format email tidak valid';
    }

    // address

    if (!form.address) {
      newErrors.address = 'Alamat wajib diisi';
    }

    // insurance fields

    if (!form.name) newErrors.name = 'Nama asuransi wajib diisi';

    if (!form.id_number) newErrors.id_number = 'ID Number wajib diisi';

    if (!form.faskes) newErrors.faskes = 'Faskes wajib diisi';

    // file validation helper

    const validateFile = (file, fieldName) => {
      if (!file) {
        newErrors[fieldName] = 'File wajib diupload';

        return;
      }

      if (file.type !== 'application/pdf') {
        newErrors[fieldName] = 'File harus PDF';
      }

      if (file.size > MAX_SIZE) {
        newErrors[fieldName] = 'Ukuran file maksimal 20MB';
      }
    };

    if (form.address_asper_ktp === 0) {
      validateFile(form.domicile_file, 'domicile_file');
    }

    validateFile(form.file, 'file');

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleReset = () => {
    setForm({
      email: '',

      address: '',

      address_asper_ktp: 0,

      domicile_file: null,

      name: 'BPJS Kesehatan',

      id_number: '',

      faskes: '',

      file: null,
    });

    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);

    await api
      .post('employees/personal-data/add', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(() => {
        showToast('Data berhasil disimpan', 'success');
        handleReset();
      })
      .catch((err) => {
        showToast(`Gagal! ${err.response?.data?.message || err.message}`, 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return isComplete ? (
    <div className="container mt-4">
      <Alert color="success" className="mb-4 rounded-4 text-center text-secondary">
        Data Personal Karyawan Sudah Dilengkapi
      </Alert>
    </div>
  ) : (
    <div className="mt-1">
      <h5 className="mb-1">Form Penambahan Data Personal Karyawan</h5>
      <div className="mb-4">
        <span className="fs-5">
          Silakan isi formulir di bawah ini untuk melengkapi data personal Anda
        </span>
      </div>
      <form onSubmit={handleSubmit} method="POST" encType="multipart/form-data">
        <div className="row g-3 border border-light pb-4 px-4 rounded-4 mt-1">
          {/* Email */}
          <div className="col-md-8">
            <Label className="form-label">Email Pribadi</Label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              className={`form-control rounded-3 ${errors.email && 'is-invalid'}`}
            />
            <div className="invalid-feedback">{errors.email}</div>
          </div>

          {/* Address */}
          <div className="col-12">
            <Label className="form-label">Alamat Lengkap</Label>
            <textarea
              name="address"
              value={form.address}
              onChange={handleChange}
              className={`form-control rounded-3 ${errors.address && 'is-invalid'}`}
              rows="3"
            />
            <div className="invalid-feedback">{errors.address}</div>
          </div>

          {/* Checkbox */}
          <div className="col-12">
            <div className="form-check">
              <input
                type="checkbox"
                name="address_asper_ktp"
                checked={form.address_asper_ktp === 1}
                onChange={handleChange}
                className={`form-check-input ${errors.address_asper_ktp && 'is-invalid'}`}
                id="ktpCheck"
              />
              <Label className="form-check-label" htmlFor="ktpCheck">
                Alamat sesuai KTP
              </Label>
            </div>
          </div>

          {/* File Domisili */}
          {form.address_asper_ktp === 0 && (
            <div className="col-md-8">
              <Label className="form-label">Lampirkan File Domisili (PDF max 20MB)</Label>
              <input
                type="file"
                name="domicile_file"
                onChange={handleChange}
                className={`form-control rounded-3 ${errors.domicile_file && 'is-invalid'}`}
                accept="application/pdf"
              />
              <div className="invalid-feedback">{errors.domicile_file}</div>
            </div>
          )}
        </div>

        <h5 className="mt-4 mb-4">Data Asuransi</h5>

        <div className="row g-3 border border-light pb-4 px-4 rounded-4">
          {/* Nama Asuransi */}
          <div className="col-md-6">
            <Label className="form-label">Nama Asuransi</Label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              className={`form-control rounded-3 ${errors.name && 'is-invalid'}`}
              readOnly
            />
            <div className="invalid-feedback">{errors.name}</div>
          </div>

          {/* ID Number */}
          <div className="col-md-6">
            <Label className="form-label">Nomor ID Asuransi</Label>
            <input
              type="text"
              name="id_number"
              value={form.id_number}
              onChange={handleChange}
              className={`form-control rounded-3 ${errors.id_number && 'is-invalid'}`}
            />
            <div className="invalid-feedback">{errors.id_number}</div>
          </div>

          {/* Faskes */}
          <div className="col-md-6">
            <Label className="form-label">Faskes</Label>
            <input
              type="text"
              name="faskes"
              value={form.faskes}
              onChange={handleChange}
              className={`form-control rounded-3 ${errors.faskes && 'is-invalid'}`}
            />
            <div className="invalid-feedback">{errors.faskes}</div>
          </div>

          {/* File Insurance */}
          <div className="col-md-6">
            <Label className="form-label">Lampirkan File Kartu Asuransi (PDF max 20MB)</Label>
            <input
              type="file"
              name="file"
              onChange={handleChange}
              className={`form-control rounded-3 ${errors.file && 'is-invalid'}`}
              accept="application/pdf"
            />
            <div className="invalid-feedback">{errors.file}</div>
          </div>
        </div>

        {/* Actions */}
        <div className="d-flex justify-content-end gap-2 mt-4">
          <button type="button" onClick={handleReset} className="btn btn-secondary rounded-3">
            Reset
          </button>

          <button type="submit" disabled={isLoading} className="btn btn-primary rounded-3">
            {isLoading ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </form>
    </div>
  );
}
